<template>
  <div>
    <popup2 :value="!!event" @input="event = null" width="428">
      <edit-field
        v-if="event === 'addGroup'"
        :type="'chips'"
        :title="'Add media to groups'"
        :field="'Groups'"
        :value="[]"
      />
      <default-confirmation
        text-delete="Delete this medias completely?"
        v-else-if="event === 'delete'"
        @close="event = null"
        @delete="deleteAction"
      />
      <component
        v-else-if="event"
        :entity="{}"
        :is="event"
        type="MEDIA"
        :callback-after="load"
        @save="saveHandler"
        @close="event = null"
      />
    </popup2>
    <table-page
      :selected-out="eValue"
      :toolbar="toolbar"
      :table="table"
      :entity="true"
      @event="eventHandler"
    />
  </div>
</template>

<script>
import pageActions from './pageActions';
import selectedActions from './selectedActions';
import DefaultConfirmation from '@/components/app-entity/modals/DefaultConfirmation';
import {
  ref,
  computed,
  onMounted,
  watch,
  toRefs,
  reactive,
  onUnmounted
} from '@vue/composition-api';
import convertBytes from '@/utils/convertBytes';
import useMedias from '@/api/medias';
import useSettings from '@/api/tableSettings';
import axios from 'axios';
import toSnakeCase from 'lodash.snakecase';
import useGroups from '@/api/groups';
import { nanoid } from 'nanoid';
import useSchemas from '@/api/schemas';
import useAuth from '@/api/auth';
import useCommonModal from '@/compositions/useCommonModal';
import { RELOAD_INTERVAL_SEC } from "@/utils/constants";
import cloneDeep from "lodash.clonedeep";

export default {
  name: 'medias',
  metaInfo: {
    titleTemplate: '%s | Media'
  },
  components: {
    DefaultConfirmation,
    addNew: () => import('@/components/app-entity/modals/CreateMedia'),
    changeSchema: () => import('@/components/app-entity/modals/EditScheme')
  },
  setup(props, { root }) {
    const { load: loadSettings, save: saveSettings } = useSettings();
    const { medias: mediasApi } = new useMedias();
    const { groups: pureGroupsApi } = new useGroups();
    const { schemas: schemasApi } = new useSchemas();
    const { profile } = useAuth();
    const modalComposition = useCommonModal();

    const {
      total,
      list,
      load: loadList,
      params,
      loading,
      setParams
    } = mediasApi;

    const visibleColumns = ref([]);
    const allColumns = ref([
      'name',
      'favourite',
      'muted',
      'enabled',
      'mediaFilename',
      'mediaSize',
      'mediaType',
      'drivername',
      'editorgroup',
      'usergroup',
      'readergroup'
    ]);

    const showOnMobile = ['favourite'];

    const eValue = ref([]);

    let timerId = null;
    function deleteAction() {
      mediasApi.remove(eValue.value.map(i => ({ id: i.id }))).then(() => {
        load();
        event.value = null;
        eValue.value = [];
      });
    }

    watch(
      () => root.$route.query.filterTable,
      () => {
        load();
      }
    );

    function load(filterOuter = {}) {
      let filter = {
        ...params.value.filter,
        ...filterOuter,
        schemaType: { equalTo: 'media' }
      };

      if (root.$route.query.filterTable !== undefined) {
        filter.name = {
          includesInsensitive: root.$route.query.filterTable
        };
      }

      setParams({
        filter
      });

      return loadList().finally(() => {
        const setts = loadSettings('mediasSettings');
        if (setts) {
          visibleColumns.value = setts;
        }
      });
    }

    const event = ref(null);
    const bufferSelected = ref([]);
    const toolbar = ref({
      title: 'medias',
      showAccess: true,
      pageActions,
      selectedActions
    });
    const settings = computed(() => {
      return visibleColumns.value;
    });
    const columns = computed(() => {
      return allColumns.value.map(item => ({
        key: item,
        text: root.$t(`object.${item}`),
        value: item,
        showOnMobile: showOnMobile.includes(item)
      }));
    });
    const items = computed(() =>
      list.value.map(i => ({
        ...i,
        t: root.$route.query.text,
        mediaSize: i.mediaSize ? convertBytes(i.mediaSize) : i.mediaSize,
        iconFallback: 'mdi-image',
        isSvg: i.mediatype?.includes('svg'),
        avatar: i.mediatype?.includes('image')
          ? `${window.__pixelConfig.APP_MEDIA_SERVER}/download/${
              i.id
            }/${localStorage.getItem('authToken')}`
          : null
      }))
    );
    const count = computed(() => total.value);
    const isLoading = computed(() => loading.value);
    const state = reactive({
      schemasList: [],
      usersGroup: [],
      table: {
        filters: [
          {
            id: 'favourite',
            value: null,
            values: [
              { name: 'All', id: null },
              { name: 'Yes', id: true },
              { name: 'No', id: false }
            ],
            title: 'Favorites'
          },
          {
            id: 'muted',
            value: null,
            values: [
              { name: 'All', id: null },
              { name: 'Yes', id: true },
              { name: 'No', id: false }
            ],
            title: 'Muted'
          },
          {
            id: 'status',
            value: null,
            values: [
              { name: 'All', id: null },
              { name: 'Enabled', id: true },
              { name: 'Disabled', id: false }
            ],
            title: 'Status'
          }
        ],
        total: count,
        items,
        columns,
        settings,
        loading: isLoading,
        itemRoute: 'mediasTableComponent'
      }
    });

    function eventHandler({ event: e, data }) {
      eValue.value = data;
      switch (e) {
        case 'setSettings': {
          visibleColumns.value = data;
          saveSettings('mediasSettings', data);
          break;
        }
        case 'delete': {
          event.value = 'delete';
          break;
        }
        case 'addNew': {
          event.value = 'addNew';
          break;
        }
        case 'changeSchema': {
          bufferSelected.value = data;
          event.value = 'changeSchema';
          break;
        }
        case 'addGroup': {
          event.value = 'addGroup';
          break;
        }
        case 'massiveDisable': {
          mediasApi
            .update(eValue.value.map(i => ({ id: i.id, enabled: false })))
            .then(() => {
              load();
              event.value = null;
              eValue.value = [];
            });
          break;
        }
        case 'massiveAccessRights': {
          modalComposition.modal.show({
            component: () =>
              import('@/components/app-entity/modals/AppDialog.vue'),
            bind: {
              nameModal: 'EditRightsGroup'
            },
            slots: {
              default: {
                component: () =>
                  import('@/components/app-entity/modals/EditRightsGroup'),
                bind: {
                  entity: null
                },
                on: {
                  close() {
                    modalComposition.modal.hide('EditRightsGroup');
                  },
                  save(e) {
                    const payload = {};

                    if (e.editorgroup) {
                      payload.editorgroup = e.editorgroup;
                    }
                    if (e.readergroup) {
                      payload.readergroup = e.readergroup;
                    }
                    if (e.usergroup) {
                      payload.usergroup = e.usergroup;
                    }

                    mediasApi
                      .update(
                        eValue.value.map(i => ({
                          id: i.id,
                          ...payload
                        }))
                      )
                      .then(() => {
                        load();
                        eValue.value = [];
                        event.value = null;
                      });
                    modalComposition.modal.hide('EditRightsGroup');
                  }
                }
              }
            }
          });
          break;
        }

        case 'massiveEnable': {
          mediasApi
            .update(eValue.value.map(i => ({ id: i.id, enabled: true })))
            .then(() => {
              load();
              event.value = null;
              eValue.value = [];
            });
          break;
        }
        case 'setFilters': {
          state.table.filters = cloneDeep(data);
          let filter = {
            schemaType: { equalTo: 'media' }
          };
          data
            .filter(i => {
              if (Array.isArray(i.model) && i.model.length > 0) {
                return true;
              } else if (!Array.isArray(i.model) && i.model.value !== null) {
                return true;
              }
              return false;
            })
            .forEach(i => {
              filter = {
                ...filter,
                ...i?.getFilter()
              };
            });
          setParams({ filter, offset: 0 });
          load(filter);
          break;
        }
        case 'filteringTable': {
          const result = {};

          if (data.itemsPerPage) {
            result.first = data.itemsPerPage;
          }

          result.offset = (data.page - 1) * data.itemsPerPage;

          if (data.desc) {
            setParams({ desc: data.desc.length ? data.desc[0] : false });
          }

          if (data.sort) {
            if (data.sort.length) {
              setParams({ sort: data.sort[0] });
            } else {
              setParams({ sort: null });
              setParams({ orderBy: 'NATURAL' });
            }
          }

          if (data.desc && params.value.sort) {
            if (params.value.sort === 'drivername') {
              result.orderBy = 'SCHEMA_NAME';
            } else {
              result.orderBy = toSnakeCase(params.value.sort).toUpperCase();
            }
            result.orderBy += `_${params.value.desc ? 'DESC' : 'ASC'}`;
          }
          setParams(result);
          load();
          break;
        }
      }
    }

    function saveHandler(data) {
      switch (event.value) {
        case 'addNew': {
          mediasApi
            .save({
              object: {
                schemaId: data.schema,
                name: data.name,
                enabled: data.enabled,
                description: data.description,
                editorgroup: data.editorgroup
              }
            })
            .then(r => {
              if (data.formData) {
                return axios
                  .request({
                    method: 'POST',
                    url: `${window.__pixelConfig.APP_MEDIA_SERVER}/upload/${
                      r.data.createObject.object.id
                    }/${localStorage.getItem('authToken')}`,
                    headers: {
                      'Content-Type': 'multipart/form-data'
                    },
                    onUploadProgress: function(progressEvent) {
                      let percentCompleted = Math.round(
                        (progressEvent.loaded * 100) / progressEvent.total
                      );
                    },
                    data: data.formData
                  })
                  .then(() => {
                    return Promise.resolve(r);
                  });
              } else {
                return Promise.resolve(r);
              }
            })
            .then(r => {
              event.value = null;
              eValue.value = [];
              root.$router.push({
                name: 'mediasTableComponent',
                params: { id: r.data.createObject.object.id }
              });
              load();
            });
          break;
        }
        case 'changeSchema': {
          mediasApi
            .update(
              bufferSelected.value.map(item => ({
                id: item.id,
                schemaId: data
              }))
            )
            .then(r => {
              event.value = null;
              eValue.value = [];
              load();
            });
          break;
        }
      }
    }

    onUnmounted(() => {
      clearInterval(timerId);
      setParams(null);
    });

    onMounted(async () => {
      setParams({
        first:
          profile.value?.objectProperties.find(
            item => item.key === 'programNumbersOfRows'
          )?.value || 20,
        filter: {
          schemaType: { equalTo: 'media' },
          hardLinked: { equalTo: false }
        }
      });

      timerId = setInterval(load, RELOAD_INTERVAL_SEC);

      const setts = loadSettings('mediasSettings');

      if (setts) {
        visibleColumns.value = setts;
      } else {
        visibleColumns.value = allColumns.value;
      }

      await load();

      await schemasApi
        .loadPureSchemata('MEDIA', {
          parentSchemaExists: false
        })
        .then(data => {
          state.schemasList = data.schemata.map(schema => ({
            id: schema.id,
            value: schema.id,
            name: schema.name
          }));
        });

      await pureGroupsApi.load().then(data => {
        state.usersGroup = data.userGroups.map(group => ({
          id: group.id,
          value: group.id,
          name: group.groupName
        }));
      });
      await root.$nextTick();
      const filters = [
        {
          id: 'favourite',
          title: 'Favorite',
          getFilter() {
            return {
              favourite: {
                in: this.model.map(item => item.value)
              }
            };
          },
          model: [],
          type: 'tags',
          values: [
            {
              value: true,
              name: 'Yes',
              id: nanoid()
            },
            {
              name: 'No',
              value: false,
              id: nanoid()
            }
          ]
        },
        {
          title: 'Muted',
          id: 'muted',
          type: 'tags',
          getFilter() {
            return {
              muted: {
                in: this.model.map(item => item.value)
              }
            };
          },
          model: [],
          values: [
            { name: 'Yes', id: nanoid(), value: true },
            { name: 'No', id: nanoid(), value: false }
          ]
        },
        {
          id: 'enabled',
          title: 'Status',
          type: 'tags',
          getFilter() {
            return {
              enabled: {
                in: this.model.map(item => item.value)
              }
            };
          },
          model: [],
          values: [
            { name: 'Enabled', id: nanoid(), value: true },
            { name: 'Disabled', id: nanoid(), value: false }
          ]
        },
        {
          id: 'type',
          title: 'Media type',
          type: 'tags',
          getFilter() {
            return {
              mediaType: {
                in: this.model.map(item => item.value)
              }
            };
          },
          model: [],
          values: [
            { name: 'png', id: nanoid(), value: 'image/png' },
            { name: 'jpg', id: nanoid(), value: 'image/jpg' },
            { name: 'jpeg', id: nanoid(), value: 'image/jpeg' },
            { name: 'svg', id: nanoid(), value: 'image/svg+xml' },
            {
              name: 'python script',
              id: nanoid(),
              value: 'text/x-python-script'
            }
          ]
        },
        {
          getFilter() {
            return {
              schemaName: {
                in: this.model.map(item => item.name)
              }
            };
          },
          id: 'Schemas name',
          model: [],
          type: 'tags',
          values: state.schemasList,
          title: 'Schema name'
        },
        {
          getFilter() {
            return {
              editorgroup: {
                in: this.model.map(item => item.id)
              }
            };
          },
          id: 'Editors group',
          model: [],
          type: 'tags',
          values: state.usersGroup,
          title: 'Editors group'
        },
        {
          getFilter() {
            return {
              usergroup: {
                in: this.model.map(item => item.id)
              }
            };
          },
          id: 'Users group',
          model: [],
          type: 'tags',
          values: state.usersGroup,
          title: 'Users group'
        },
        {
          getFilter() {
            return {
              readergroup: {
                in: this.model.map(item => item.id)
              }
            };
          },
          id: 'Readers group',
          model: [],
          type: 'tags',
          values: state.usersGroup,
          title: 'Readers group'
        }
      ];
      state.table.filters = cloneDeep(filters);
      state.table.emptyFilters = cloneDeep(filters);
    });
    return {
      ...toRefs(state),
      event,
      toolbar,
      eventHandler,
      saveHandler,
      loading: mediasApi.loading,
      deleteAction,
      eValue,
      load
    };
  }
};
</script>
